import { useState, cloneElement } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, PartyPopper } from "lucide-react";
import { submitConsultation, submitDemo } from "@/lib/leads";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export const BookDemoDialog = ({ trigger, defaultCourse = "IELTS", leadType = "coaching" }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    course: defaultCourse,
    message: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Immigration leads → consultation route (ridkab); Coaching leads → demo route.
      const send = leadType === "immigration" ? submitConsultation : submitDemo;
      await send(form);
      setDone(true);
      toast.success("Your booking is in! Our team will call you within 24 hrs.");
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const wrappedTrigger = cloneElement(trigger, {
    onClick: () => {
      setDone(false);
      setForm((f) => ({ ...f, name: user?.name || f.name, email: user?.email || f.email, course: defaultCourse }));
      setOpen(true);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{wrappedTrigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg rounded-3xl border-brand-navy/10">
        {done ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 mx-auto rounded-full bg-brand-red/10 text-brand-red grid place-items-center">
              <PartyPopper className="w-8 h-8" />
            </div>
            <DialogTitle className="mt-5 font-heading text-2xl text-brand-navy">You&#39;re booked!</DialogTitle>
            <DialogDescription className="mt-2 text-brand-navy/60">
              We&#39;ll call you on <span className="font-semibold text-brand-navy">{form.phone}</span> within 24 hours to schedule your free demo.
            </DialogDescription>
            <Button data-testid="demo-close-btn" onClick={() => setOpen(false)} className="mt-6 rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold px-6">
              Great, close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-brand-navy">Book a Free Demo</DialogTitle>
              <DialogDescription className="text-brand-navy/60">
                One 30-min session with a mentor. Zero cost, zero pressure.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="mt-4 space-y-4" data-testid="demo-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-brand-navy font-semibold">Name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="demo-name" />
                </div>
                <div>
                  <Label className="text-brand-navy font-semibold">Phone</Label>
                  <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 …" className="mt-1 h-11 rounded-xl" data-testid="demo-phone" />
                </div>
              </div>
              <div>
                <Label className="text-brand-navy font-semibold">Email</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="demo-email" />
              </div>
              <div>
                <Label className="text-brand-navy font-semibold">Course</Label>
                <Input required value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="demo-course" />
              </div>
              <div>
                <Label className="text-brand-navy font-semibold">Anything we should know? (optional)</Label>
                <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 rounded-xl" data-testid="demo-message" />
              </div>
              <Button type="submit" disabled={loading} data-testid="demo-submit-btn" className="w-full h-12 rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold btn-scale">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Book my demo"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
